import { useProfile } from '@/contexts/CurrentUserContext/hooks';
import type { PageExperience } from '@/features/experiences/types';
import { OrganizationType, UserTier } from '@/types/gql.generated';
import type { GateRequirement, GateScope } from './gates';

type Entitlement = {
  /** Whether the resolved subject meets the requirement. */
  isEntitled: boolean;
  /** The org type behind the decision, when there is a single one. */
  orgType: OrganizationType | null;
  isLoading: boolean;
};

/**
 * Resolves whether a gate's requirement is met, for one of the two scopes.
 *
 * `user` reads the signed-in user's tier, which the server already computes as
 * max(seatTier, inheritedSeatTier) of their personal org — so Plus inherited from a
 * workspace membership counts without any extra work here. `family` and `business`
 * need more than a tier, so they look through the user's memberships.
 *
 * `experience` reads the experience's home org directly. An experience with no home
 * org is treated as free. Every stored experience now has one — Experience.organizationId
 * is NOT NULL — but `org` is still absent on the two read paths that withhold it: a
 * magic-job preview is fabricated in memory and never homed, and a published read
 * deliberately omits org linkage so an anonymous visitor can't see the owner's billing
 * posture. So this fallback is still load-bearing; don't drop it.
 *
 * Reads the profile query rather than CurrentUserContext deliberately. It's the same
 * data — the context is a proxy to this hook and react-query dedupes the request —
 * but it doesn't throw without a provider, so a gate can be resolved from anywhere,
 * including a component rendered in isolation by a test.
 */
export const useGateTier = (
  scope: GateScope,
  requires: GateRequirement,
  experience: PageExperience | undefined
): Entitlement => {
  const { currentUser, isLoading } = useProfile();

  if (scope === 'experience') {
    const org = experience?.org ?? null;
    const isPro = org?.effectiveTier === UserTier.Pro;

    return {
      isEntitled: isPro && meetsPlan(requires, org?.type),
      orgType: org?.type ?? null,
      // The experience is already in hand; nothing to wait on.
      isLoading: false,
    };
  }

  const isPro = currentUser?.tier === UserTier.Pro;

  if (requires === 'plus') {
    return { isEntitled: isPro, orgType: null, isLoading };
  }

  // A specific plan can't be read off the user's tier, so find a membership that
  // both grants entitlement and is of the required kind. The personal org is
  // excluded by the type check — it never carries a FAMILY or BUSINESS plan itself.
  const requiredType =
    requires === 'family' ? OrganizationType.Family : OrganizationType.Business;
  const grantingOrg = (currentUser?.organizations ?? []).find(
    (org) =>
      org.type === requiredType &&
      org.effectiveTier === UserTier.Pro &&
      meetsPlan(requires, org.type)
  );

  return {
    isEntitled: Boolean(grantingOrg),
    orgType: grantingOrg?.type ?? null,
    isLoading,
  };
};

/**
 * `plus` is satisfied by tier alone. The other two are satisfied by the org's type
 * and nothing else: the funding plan says who pays, not what kind of workspace this
 * is, and the two come apart on the org that matters most here. A family buyer's
 * PERSONAL org shares the FAMILY subscription that funds their family workspace, so
 * it reports `subscriptionPlan: FAMILY` while being nobody's family — accepting that
 * plan here is what put text reminders on personal planners (AH-2471).
 */
const meetsPlan = (
  requires: GateRequirement,
  orgType: OrganizationType | null | undefined
): boolean => {
  switch (requires) {
    case 'plus':
      return true;
    case 'family':
      return orgType === OrganizationType.Family;
    case 'business':
      return orgType === OrganizationType.Business;
    default: {
      // Adding a requirement without deciding what satisfies it should fail to
      // compile rather than silently inheriting another tier's rule.
      const exhaustive: never = requires;
      return exhaustive;
    }
  }
};
